/* /crm/projects/:projectId/businesscase — de knop "Businesscase" bij een project.
 *
 * Heeft het project een (niet-gearchiveerde) businesscase, dan opent de nieuwste. Zo niet,
 * dan staat hier dat er nog geen is, met een knop om er een aan te maken. Tot 29-09-2026
 * maakte deze route er stil een lege "Businesscase v1" bij; zo ontstonden de lege cases
 * van P17, P23, P28 en P39 (ADR 0023). */

import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useRechten } from '@/features/relaties/ui/Onderdelen';
import { BUSINESSCASES_SLEUTEL } from '@/features/businesscases/data/sleutels';

const client = supabase as any;

export default function CRMProjectBusinesscase() {
  const { projectId } = useParams<{ projectId: string }>();
  const { magBeheren } = useRechten();
  const q = useQuery({
    queryKey: [...BUSINESSCASES_SLEUTEL, 'project', projectId],
    enabled: Boolean(projectId),
    queryFn: async () => {
      const [bcs, project] = await Promise.all([
        client.from('businesscases').select('id, created_at, is_archived').eq('project_id', projectId).eq('is_archived', false).order('created_at', { ascending: false }),
        client.from('projects').select('id, name, project_number').eq('id', projectId).maybeSingle(),
      ]);
      if (bcs.error) throw bcs.error;
      if (project.error) throw project.error;
      return { nieuwste: (bcs.data?.[0]?.id as string | undefined) ?? null, project: project.data as { id: string; name: string; project_number: string | null } | null };
    },
  });

  if (q.isPending) {
    return <Card role="status"><CardContent className="p-6 text-sm text-muted-foreground">Businesscase zoeken…</CardContent></Card>;
  }
  if (q.error) {
    return (
      <Card role="alert"><CardContent className="space-y-3 p-6">
        <p className="text-sm text-destructive">De businesscase kon niet worden gezocht: {(q.error as Error).message}</p>
        <Button size="sm" onClick={() => q.refetch()}>Opnieuw proberen</Button>
      </CardContent></Card>
    );
  }
  if (q.data.nieuwste) return <Navigate to={`/crm/businesscases/${q.data.nieuwste}`} replace />;

  const p = q.data.project;
  const naam = p ? [p.project_number, p.name].filter(Boolean).join(' · ') : 'Dit project';
  return (
    <Card data-geen-businesscase>
      <CardContent className="flex items-start gap-3 p-6">
        <Calculator className="mt-0.5 h-5 w-5 text-muted-foreground" aria-hidden />
        <div className="min-w-0 space-y-2">
          <h1 className="text-lg font-semibold">{naam} heeft nog geen businesscase</h1>
          <p className="text-sm text-muted-foreground">
            {magBeheren
              ? 'Maak er een aan als je het rendement voor de klant wilt doorrekenen. Hij begint met tien lege jaren; opbrengsten komen uit een Zympler-export of vul je per jaar in.'
              : 'Een beheerder kan er een aanmaken.'}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {magBeheren && p && (
              <Button size="sm" asChild><Link to={`/crm/businesscases?project=${p.id}`}>Businesscase aanmaken</Link></Button>
            )}
            {p && <Button size="sm" variant="outline" asChild><Link to={`/crm/projects/${p.id}`}>Terug naar het project</Link></Button>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
